import React, { memo, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import * as zod from 'zod';
import {
  AiAgentSessionGoalStatus,
  AiAgentSessionType,
  AiAgentSessionWhereInput,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useSetFormFieldValue } from 'src/components/filters/helpers';
import { ReactFilters } from 'src/components/filters/react-filters';
import { Select, SelectOption } from 'src/components/general/inputs/select/select';
import {
  StyledTableFiltersForm,
  TableLayoutFilterProps,
  useSimpleTableSubmitHandler,
} from 'src/components/general/table-helpers';

const CHANNELS = ['CHAT', 'VOICE'] as const satisfies readonly AiAgentSessionType[];
const GOAL_ACCOMPLISHMENTS = [
  'ACHIEVED',
  'PARTIALLY_ACHIEVED',
  'NOT_ACHIEVED',
] as const satisfies readonly AiAgentSessionGoalStatus[];

const urlEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  zod.enum(values).nullish().catch(null);

export const sessionsFilterSchema = zod.object({
  channel: urlEnum(CHANNELS),
  goalAccomplishment: urlEnum(GOAL_ACCOMPLISHMENTS),
});

export type SessionsFiltersFormValues = zod.infer<typeof sessionsFilterSchema>;

const defaultFilters: SessionsFiltersFormValues = {
  channel: null,
  goalAccomplishment: null,
};

export const toSessionsWhere = (
  filters: SessionsFiltersFormValues,
): AiAgentSessionWhereInput => ({
  ...(filters.channel ? { type: { _eq: filters.channel } } : {}),
  ...(filters.goalAccomplishment
    ? { goalStatus: { _eq: filters.goalAccomplishment } }
    : {}),
});

export const SessionsFilters = memo<TableLayoutFilterProps<SessionsFiltersFormValues>>(
  ({ visible, filters, onClose }) => {
    const { t } = useTranslation(['common', 'aiAgents']);
    const submitHandler = useSimpleTableSubmitHandler<SessionsFiltersFormValues>({
      onClose,
    });
    const history = useHistory();
    const location = useLocation();

    const form = useForm<SessionsFiltersFormValues>({
      resolver: zodResolver(sessionsFilterSchema),
      values: filters,
      defaultValues: defaultFilters,
    });
    const { control, handleSubmit, setValue, reset } = form;
    const { setFormFieldValue } = useSetFormFieldValue<SessionsFiltersFormValues>({
      defaultValues: defaultFilters,
      setValue,
    });

    useEffect(() => {
      if (filters) {
        reset(filters);
      }
    }, [filters, reset]);

    const channelOptions: SelectOption<(typeof CHANNELS)[number]>[] = CHANNELS.map(
      (value) => ({
        value,
        label: t(`aiAgents:pages.agentDetails.sessions.filters.channel.options.${value}`),
      }),
    );
    const goalOptions: SelectOption<(typeof GOAL_ACCOMPLISHMENTS)[number]>[] =
      GOAL_ACCOMPLISHMENTS.map((value) => ({
        value,
        label: t(
          `aiAgents:pages.agentDetails.sessions.filters.goalAccomplishment.options.${value}`,
        ),
      }));

    return (
      <ReactFilters
        form={form}
        onSave={() => {
          void handleSubmit(submitHandler)();
        }}
        onReset={() => {
          onClose();
          reset(defaultFilters);
          history.push(location.pathname);
        }}
        filters={filters}
        visible={visible}
        onClose={onClose}
        testId="ai-agent-sessions-filters"
        applyFiltersLabel={t('aiAgents:pages.agentDetails.sessions.filters.apply')}
        scrollOnOverflow
      >
        <StyledTableFiltersForm>
          <Controller
            control={control}
            name="channel"
            render={({ field }) => (
              <Select
                data-lgg-id="ai-agent-sessions-filters-field-channel"
                name="ai-agent-sessions-filters-field-channel"
                value={channelOptions.find(({ value }) => value === field.value) ?? null}
                options={channelOptions}
                placeholder={t('common:selectOne')}
                isSearchable={false}
                label={t('aiAgents:pages.agentDetails.sessions.filters.channel.title')}
                onChange={(option) => setFormFieldValue(field.name, option?.value)}
              />
            )}
          />
          <Controller
            control={control}
            name="goalAccomplishment"
            render={({ field }) => (
              <Select
                data-lgg-id="ai-agent-sessions-filters-field-goal-accomplishment"
                name="ai-agent-sessions-filters-field-goal-accomplishment"
                value={goalOptions.find(({ value }) => value === field.value) ?? null}
                options={goalOptions}
                placeholder={t('common:selectOne')}
                isSearchable={false}
                label={t(
                  'aiAgents:pages.agentDetails.sessions.filters.goalAccomplishment.title',
                )}
                onChange={(option) => setFormFieldValue(field.name, option?.value)}
              />
            )}
          />
        </StyledTableFiltersForm>
      </ReactFilters>
    );
  },
);
